import { useCallback, useEffect, useMemo } from 'react'

import { useUnit } from 'effector-react'
import { Controller, FormProvider, useForm } from 'react-hook-form'

import { Error, useErrorReset } from '@/components/ui/Error'
import { Button } from '@/components/ui/button.tsx'
import { Dialog } from '@/components/ui/dialog.tsx'
import { TextField } from '@/components/ui/text-field.tsx'
import { $account } from '@/models/account.ts'
import { AssigneeSelector } from '@/modules/tasks/components/create-task-dialog/assignee-selector'
import { RepetitionSelector } from '@/modules/tasks/components/task-scheduler-dialog/repetition-selector.tsx'
import type { SchedulerEditorValues } from '@/modules/tasks/components/task-scheduler-dialog/schema.ts'
import {
  defaultValues,
  schedulerResolver,
} from '@/modules/tasks/components/task-scheduler-dialog/schema.ts'
import { useSaveRepeatable } from '@/modules/tasks/query/use-save-task'
import type {
  RepeatableTask,
  Weekday,
} from '@/modules/tasks/service/repeatable-task-service.ts'
import { weekdays } from '@/modules/tasks/service/repeatable-task-service.ts'

export interface TaskEditorDialogProps {
  scheduler?: RepeatableTask
  isOpen: boolean
  onVisibilityChange: (state: boolean) => void
}

export function rateToDaysOfWeek(rate: number[]): Weekday[] {
  return weekdays.filter((_, index) => {
    if (!rate || index >= rate.length) {
      return false
    }

    return rate[index] === 1
  })
}

export function rateToDayOfMonth(rate: number[]): number {
  return rate[0] ?? 0
}

export function rateToDayAndMonth(rate: number[]): [number, number] {
  return [rate[0] ?? 0, rate[1] ?? 0]
}

function readRate(
  scheduler: RepeatableTask | null | undefined,
): Partial<SchedulerEditorValues> {
  if (!scheduler) {
    return {}
  }
  switch (scheduler.period) {
    case 'Daily':
      return {}
    case 'Weekly':
      return {
        rateDaysOfWeek: rateToDaysOfWeek(scheduler.rate ?? []),
      }
    case 'Monthly':
      return {
        rateDayOfMonth: rateToDayOfMonth(scheduler.rate ?? []),
      }
    case 'Annually': {
      const [day, month] = rateToDayAndMonth(scheduler.rate ?? [])

      return {
        rateDayOfMonth: day,
        rateMonth: month,
      }
    }
    default:
      return {}
  }
}

export const TaskSchedulerDialog = (props: TaskEditorDialogProps) => {
  const { isOpen, onVisibilityChange, scheduler } = props
  const account = useUnit($account)

  const handleClose = useCallback(
    () => onVisibilityChange(false),
    [onVisibilityChange],
  )

  const form = useForm<SchedulerEditorValues>({
    defaultValues: {
      ...defaultValues,
      ...scheduler,
      ...readRate(scheduler),
      assignees:
        scheduler?.assignees?.map((it) => it.id) ??
        (account ? [account?.userId] : []),
    },
    resolver: schedulerResolver,
  })

  useEffect(() => {
    form.reset({
      ...defaultValues,
      ...scheduler,
      ...readRate(scheduler),
      assignees:
        scheduler?.assignees?.map((it) => it.id) ??
        (account ? [account?.userId] : []),
    })
  }, [scheduler, form, isOpen, account])

  const save = useSaveRepeatable()

  const handleSave = useCallback(
    (state: SchedulerEditorValues) => {
      save.mutate(state).then(([status]) => {
        if (status === 'Ok') {
          form.reset()
          handleClose()
        }
      })
    },
    [form, handleClose, save],
  )

  const [error, resetError] = useErrorReset(save.error)

  useEffect(() => resetError(), [isOpen, resetError])

  const mode = useMemo(() => (scheduler ? 'edit' : 'create'), [scheduler])

  return (
    <Dialog
      panelClassName="sm:w-full sm:mx-2 sm:max-w-[515px]"
      show={isOpen}
      onClose={handleClose}
    >
      <FormProvider {...form}>
        <Dialog.Header>
          <Dialog.Title className="text-center">
            {mode === 'edit' ? 'Edit Scheduler' : 'Create new Scheduler'}
          </Dialog.Title>
          <Dialog.Description className="text-center">
            Schedulers automatically create tasks at defined intervals.
          </Dialog.Description>
        </Dialog.Header>
        <form onSubmit={form.handleSubmit(handleSave)}>
          <div className="mt-[24px] flex flex-col gap-[24px]">
            <Controller
              name="name"
              render={({ field, fieldState: { error } }) => (
                <div className="flex flex-col gap-2">
                  <label
                    htmlFor="name-field"
                    className="text-sm font-medium text-title"
                  >
                    Title
                  </label>
                  <TextField
                    id="name-field"
                    placeholder="Enter Title"
                    {...field}
                  />
                  {error && (
                    <p className="text-xs text-danger-default">
                      {error.message}
                    </p>
                  )}
                </div>
              )}
            />
            <Controller
              name="detail"
              render={({ field, fieldState: { error } }) => (
                <div className="flex flex-col gap-2">
                  <label
                    htmlFor="detail-field"
                    className="text-sm font-medium text-title"
                  >
                    Note
                  </label>
                  <textarea
                    id="detail-field"
                    rows={5}
                    className="w-full rounded-md border bg-card px-3 py-2 text-title focus:border-primary-500 focus:outline-0 focus:outline-primary-500"
                    placeholder="Enter Note"
                    {...field}
                  />
                  {error && (
                    <p className="text-xs text-danger-default">
                      {error.message}
                    </p>
                  )}
                </div>
              )}
            />
            <AssigneeSelector />
            <RepetitionSelector />
            <Controller
              name="hourlyDueDuration"
              render={({ field, fieldState: { error } }) => (
                <div className="flex flex-col gap-2">
                  <label
                    htmlFor="hourly-due-duration-field"
                    className="text-sm font-medium text-title"
                  >
                    Due Time
                  </label>
                  <TextField
                    id="hourly-due-duration-field"
                    placeholder="Enter Hourly Due Duration"
                    type="number"
                    className="w-[240px] font-normal"
                    {...field}
                  />
                  <span className="block text-xs text-secondary-300">
                    Adjust the due time accordingly in hours
                  </span>
                  {error && (
                    <p className="text-xs text-danger-default">
                      {error.message}
                    </p>
                  )}
                </div>
              )}
            />
          </div>
          <Error
            error={error}
            closable
            className="mt-4"
          />
          <Dialog.Footer>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              className="w-full"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex w-full items-center gap-2"
              isLoading={save.isLoading}
            >
              {mode === 'edit' ? 'Save' : 'Create'}
            </Button>
          </Dialog.Footer>
        </form>
      </FormProvider>
    </Dialog>
  )
}
